<template>
  <div class="w-full p-4">
    <!-- Everything above the rows travels with the page, the way it does on
         My Expenses: the filters are what you reach for after scrolling, and
         the row counts are the answer to the question you scrolled to ask.
         -mx-4 px-4 so the band reaches the window edges inside a padded
         page, and an opaque ground so rows do not show through it.
         -mt-4 matters as much as either. The page is padded p-4 and the
         navbar above it is 56px, so left alone this block begins 72px down
         and pins at 56 -- 16px of travel, which reads as the page sliding a
         little before it catches. Pulled flush with the top of the content
         it starts where it is going to stop, and never moves at all. The
         pt-3 inside is what keeps the heading off the navbar.
         pb-4 rather than pb-1 for the same reason at the other end: the gap
         between the filters and the first sprint has to belong to this
         block, so that it is measured into topStickyHeight. Left as a margin
         on the card below, it was 16px the sprint strip had to travel before
         it could pin. -->
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <div class="flex items-center gap-1.5">
        <h2 class="text-lg font-semibold">Backlog</h2>
        <!-- How the board works is worth saying once and then getting out
             of the way: as a paragraph it cost three lines above the
             filters every time anyone opened the page. -->
        <span class="relative group">
          <information-circle-icon
            class="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help"
          />
          <span
            class="hidden group-hover:block absolute left-0 top-5 z-30 w-72 rounded-md
                   border border-gray-400 bg-white p-2 text-xs text-gray-600 shadow-lg"
          >
            Earliest sprint first, in the order you arrange them. Drag onto a
            row to place before it, or onto a sprint to move it there. Click a
            title to open it, double-click beside one to rename it in place,
            double-click a member to reassign. Click a sprint or owner heading
            to fold it away. The numbers down the left run 1 upwards across
            everything these filters match, folded away or not, so anyone on
            the same filters is looking at the same row 14.
          </span>
        </span>
      </div>
      <div class="flex items-center gap-4">
        <!-- Two numbers, because they answer different questions: how much is
             on screen, and how much the filters match in the database. They
             differ whenever a window holds more than one page. -->
        <div class="text-sm text-gray-600 text-right">
          <div>
            {{ shownCount }} shown
            <span v-if="matching > items.length">of {{ matching }} matching</span>
            <span v-else>of {{ matching }} stor{{ matching === 1 ? 'y' : 'ies' }}</span>
          </div>
          <!-- Present only once a poll has actually landed, so it reads as
               evidence that live updates are working rather than as a
               permanent fixture that happens to be stale. -->
          <div v-if="pollLastAt" class="text-xs text-gray-500 tabular-nums">
            updated {{ pollLastAt }}
          </div>
        </div>
        <button
          class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white
                 hover:bg-accent-secondary flex items-center gap-1 whitespace-nowrap"
          title="Add a work item to the backlog"
          @click="startCreate"
        >
          <plus-icon class="h-4 w-4" />
          New work item
        </button>
      </div>
    </div>

    <!-- Labels above each control. <label> rather than a div so clicking
         the text focuses the control it names. items-end keeps the row
         aligned on the controls, since the labels add height above.
         Control metrics are My Expenses': h-9, border-gray-400, rounded-md. -->
    <div class="flex flex-row flex-wrap gap-3 mb-3 items-end text-sm">
      <label class="block">
        <span class="block text-xs text-gray-500 mb-0.5">Search</span>
        <input
          v-model="search"
          type="search"
          placeholder="Title &amp; description…"
          class="h-9 px-2 border border-gray-400 rounded-md w-56"
          @keyup.enter="load"
        />
      </label>
      <!-- Multi-select, because "in progress and test/review" is a question
           people actually ask of a board. A div rather than a label: the
           control is not a native input for a label to focus. -->
      <div class="w-60">
        <!-- The shortcut sits on the label line rather than in the row of
             controls: it is not a filter of its own, it is a way of setting
             the one underneath it, and the row has no width to spare. -->
        <div class="flex items-baseline justify-between gap-1 mb-0.5">
          <span class="text-xs text-gray-500">Status</span>
          <button
            class="text-xs underline"
            :class="activeOnly
              ? 'text-dark-primary font-semibold'
              : 'text-gray-500 hover:text-gray-700'"
            :title="activeOnly
              ? 'Show every status again'
              : 'Show Not Started, Next and In Progress only'"
            @click="toggleActiveOnly"
          >
            Active work
          </button>
        </div>
        <!-- The control is two rows tall: a chip box and a search strip that
             holds its height even while the input in it is hidden. Left in
             flow it stands 30px taller than every native select beside it and
             drags the whole row's alignment with it, so it is pinned to the
             box height and allowed to open downwards over the content. -->
        <div class="relative h-9">
          <!-- :compact bound rather than bare -- TypeAheadMultiSelectBox
               declares its props as an array, so Vue does no boolean casting
               and a valueless `compact` would arrive as the empty string and
               read as false. Compact shows a count in the box instead of the
               chips; which statuses they are is spelled out underneath.

               Copied on the way in, which is not a nicety. The component
               seeds its own `selected` from this very array, then push()es
               and splice()s it in place and emits that same array back --
               so `status = $event` assigned the ref the object it already
               held. The chips re-rendered, because a ref's array is deeply
               reactive, but the watcher below compares identity and saw no
               change: picking a status quietly never reloaded the board,
               which then showed every status while claiming to show one.
               A copy gives the ref a new identity and the watcher something
               to see. -->
          <type-ahead-multiselect
            class="absolute inset-x-0 top-0 z-30"
            :options="STATUSES"
            :default-value="status"
            :compact="true"
            no-selection-message="All statuses"
            placeholder="Status"
            @update-value="status = [...$event]"
          />
        </div>
      </div>
      <!-- A div with an explicit label, not a <label> wrapping the lot: the
           shortcuts are buttons, and a button inside a label activates the
           label's control as well as itself. -->
      <div>
        <div class="flex items-baseline justify-between gap-1 mb-0.5">
          <label for="backlog-owner" class="text-xs text-gray-500">Owner</label>
          <span class="flex items-baseline gap-1.5">
            <!-- Hidden rather than disabled while the user list is still on
                 its way: a My button that sets the filter to nobody is
                 worse than no button. -->
            <button
              v-if="myUserId !== null"
              class="text-xs underline"
              :class="ownerIsMe
                ? 'text-dark-primary font-semibold'
                : 'text-gray-500 hover:text-gray-700'"
              :title="ownerIsMe ? 'Show every owner again' : 'Show only my work'"
              @click="showMyWork"
            >
              My
            </button>
            <button
              class="text-xs underline"
              :class="assignee === ''
                ? 'text-dark-primary font-semibold'
                : 'text-gray-500 hover:text-gray-700'"
              title="Show every owner"
              @click="assignee = ''"
            >
              All
            </button>
          </span>
        </div>
        <!-- The four who own this backlog, and then whoever else owns
             something in the window you are looking at. Grouped rather than
             merged, so a name that is only here because of what is on
             screen does not read as a standing choice. -->
        <select id="backlog-owner" v-model="assignee" class="h-9 px-2 border border-gray-400 rounded-md">
          <option value="">All owners</option>
          <option
            v-for="o in filterOwners.named"
            :key="o.id"
            :value="o.id"
          >{{ o.label }}</option>
          <optgroup v-if="filterOwners.others.length" label="Others with work here">
            <option
              v-for="o in filterOwners.others"
              :key="o.id"
              :value="o.id"
            >{{ o.label }}</option>
          </optgroup>
        </select>
      </div>
      <!-- Two windows out from the current sprint rather than one range,
           because looking back and looking ahead are different questions. -->
      <div>
        <div class="flex items-baseline justify-between gap-1 mb-0.5">
          <label for="backlog-sprints-back" class="text-xs text-gray-500">Sprints back</label>
          <button
            class="text-xs underline"
            :class="sprintsBack === SPRINTS_BACK_SHORTCUT
              ? 'text-dark-primary font-semibold'
              : 'text-gray-500 hover:text-gray-700'"
            :title="sprintsBack === SPRINTS_BACK_SHORTCUT
              ? 'Back to the current sprint only'
              : `Look ${SPRINTS_BACK_SHORTCUT} sprints back`"
            @click="toggleSprintsBack"
          >
            {{ SPRINTS_BACK_SHORTCUT }}
          </button>
        </div>
        <select
          id="backlog-sprints-back"
          v-model.number="sprintsBack"
          class="h-9 px-2 border border-gray-400 rounded-md"
        >
          <option v-for="n in SPRINT_WINDOW" :key="`b${n}`" :value="n">
            {{ n === 0 ? 'Current sprint' : n }}
          </option>
          <!-- Last on the list because it is the widest window on it, and
               named for where it stops rather than for a count: how many
               sprints back the first one is depends on the sprint list, not
               on anything chosen here. -->
          <option :value="SPRINT_WINDOW_ALL">Earliest</option>
        </select>
      </div>
      <div>
        <div class="flex items-baseline justify-between gap-1 mb-0.5">
          <label for="backlog-sprints-forward" class="text-xs text-gray-500">Sprints forward</label>
          <button
            class="text-xs underline"
            :class="sprintsForward === SPRINTS_FORWARD_SHORTCUT
              ? 'text-dark-primary font-semibold'
              : 'text-gray-500 hover:text-gray-700'"
            :title="sprintsForward === SPRINTS_FORWARD_SHORTCUT
              ? 'Back to the current sprint only'
              : `Look ${SPRINTS_FORWARD_SHORTCUT} sprints ahead`"
            @click="toggleSprintsForward"
          >
            {{ SPRINTS_FORWARD_SHORTCUT }}
          </button>
        </div>
        <select
          id="backlog-sprints-forward"
          v-model.number="sprintsForward"
          class="h-9 px-2 border border-gray-400 rounded-md"
        >
          <option v-for="n in SPRINT_WINDOW" :key="`f${n}`" :value="n">
            {{ n === 0 ? 'Current sprint' : n }}
          </option>
          <option :value="SPRINT_WINDOW_ALL">Latest</option>
        </select>
      </div>
      <label class="block">
        <span class="block text-xs text-gray-500 mb-0.5">Blocked &amp; flagged</span>
        <select v-model="overlay" class="h-9 px-2 border border-gray-400 rounded-md">
          <option value="">Any</option>
          <option value="blocked">Blocked only</option>
          <option value="flagged">Flagged only</option>
        </select>
      </label>
      <label class="block">
        <span class="block text-xs text-gray-500 mb-0.5">Type</span>
        <!-- No @change: the watcher below reloads on every filter, and two
             handlers would fire two requests for one choice. The ref and the
             API parameter are still `kind`, which is what the field is
             called on WorkItem; only the word on screen changed. -->
        <select v-model="kind" class="h-9 px-2 border border-gray-400 rounded-md">
          <option value="">All Types</option>
          <option value="epic">Epics</option>
          <option value="milestone">Milestones</option>
          <option value="story">Stories</option>
          <option value="task">Tasks</option>
        </select>
      </label>
      <!-- Narrow and wrapped: at full width this one label took as much
           room as two dropdowns. -->
      <label class="flex items-start gap-1.5 pb-1.5 leading-tight max-w-[8.5rem]">
        <input v-model="showNoSprint" type="checkbox" class="rounded mt-0.5" />
        <span>Show work items with no sprint assigned</span>
      </label>
      <button
        class="h-9 w-9 border border-gray-400 rounded-md flex items-center justify-center
               text-gray-600 hover:bg-gray-100"
        title="Refresh"
        aria-label="Refresh"
        @click="load"
      >
        <arrow-path-icon class="h-4 w-4" />
      </button>
      <!-- Polling rather than pushing: there is no socket in this stack, and
           five seconds of staleness is well inside what a planning
           conversation needs. Clicking it again while it runs tops the clock
           up by five minutes, to a ceiling of fifteen -- see POLL_MAX_MS. -->
      <button
        class="h-9 px-2.5 border rounded-md flex items-center gap-1.5"
        :class="polling
          ? 'border-green-600 bg-green-50 text-green-800 hover:bg-green-100'
          : 'border-gray-400 text-gray-600 hover:bg-gray-100'"
        :title="polling
          ? (pollExtendable
            ? 'Picking up changes every 5 seconds — click to add 5 more minutes'
            : 'Picking up changes every 5 seconds — already at the 15 minute maximum')
          : 'Pick up changes from other people every 5 seconds, for 5 minutes'"
        @click="startPolling"
      >
        <span
          v-if="polling"
          class="h-2 w-2 rounded-full bg-green-600 animate-pulse shrink-0"
        ></span>
        <bolt-icon v-else class="h-4 w-4" />
        <span v-if="polling" class="tabular-nums whitespace-nowrap">
          Live · {{ pollRemainingLabel }} left
        </span>
        <span v-else class="whitespace-nowrap">Live updates</span>
      </button>
      <!-- A view control rather than a filter: it asks the server for
           nothing, and only rearranges what is already here. Kept with the
           buttons instead of in the labelled row of filters for that
           reason -- everything in that row costs a round trip. -->
      <button
        class="h-9 px-2.5 border rounded-md flex items-center gap-1.5"
        :class="groupByOwner
          ? 'border-dark-primary bg-dark-primary text-white hover:bg-dark-primary'
          : 'border-gray-400 text-gray-600 hover:bg-gray-100'"
        :title="groupByOwner
          ? 'List each sprint in its own order again'
          : 'Group the rows under each sprint by owner'"
        @click="groupByOwner = !groupByOwner"
      >
        <user-group-icon class="h-4 w-4" />
        <span class="whitespace-nowrap">By owner</span>
      </button>
      <!-- Named views, as opposed to the shortcuts on the filter labels:
           each one sets several filters at once, so it belongs with the
           buttons rather than above any single control. All of them toggle
           back to the Active work view, so none is a one-way door. -->
      <button
        class="h-9 px-2.5 border rounded-md flex items-center gap-1.5"
        :class="teamView
          ? 'border-dark-primary bg-dark-primary text-white hover:bg-dark-primary'
          : 'border-gray-400 text-gray-600 hover:bg-gray-100'"
        :title="teamView
          ? 'Back to the work that is on'
          : `Everything from ${TEAM_VIEW_BACK} sprints back to ${TEAM_VIEW_FORWARD} ahead \u2014 every status, owner and type \u2014 grouped by owner`"
        @click="showTeamView"
      >
        <users-icon class="h-4 w-4" />
        <span class="whitespace-nowrap">Team view</span>
      </button>
      <button
        class="h-9 px-2.5 border rounded-md flex items-center gap-1.5"
        :class="upcomingMilestones
          ? 'border-amber-600 bg-amber-50 text-amber-900 hover:bg-amber-100'
          : 'border-gray-400 text-gray-600 hover:bg-gray-100'"
        :title="upcomingMilestones
          ? 'Back to the work that is on'
          : `Every milestone from here to ${SPRINT_WINDOW_MAX} sprints ahead, any status`"
        @click="showUpcomingMilestones"
      >
        <flag-icon class="h-4 w-4" />
        <span class="whitespace-nowrap">Upcoming milestones</span>
      </button>
      <button
        class="h-9 px-2.5 border border-gray-400 rounded-md flex items-center gap-1.5
               text-gray-600 hover:bg-gray-100"
        title="Back to the work that is on: Not Started, Next and In Progress, every owner, this sprint only, any type"
        @click="resetFilters"
      >
        <arrow-uturn-left-icon class="h-4 w-4" />
        <span class="whitespace-nowrap">Reset</span>
      </button>

      <!-- The one button in this row that changes data rather than the
           view, so it is bordered in amber, sits at the end, and asks
           before it does anything. The question it asks names the number of
           items, which the server counts first and writes nothing for: on
           a board this size "are you sure" is worth very little, and "sixty
           nine items" is worth something. -->
      <template v-if="sweepCount === null">
        <button
          class="h-9 px-2.5 border border-amber-500 rounded-md flex items-center gap-1.5
                 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
          :disabled="sweeping"
          title="Clear the sprint week on every item still at Backlog that is sitting in a dated sprint. Work past Backlog, anything archived, and anything parked in an undated sprint such as 'As Needed or Opportunistic', is left alone."
          @click="askSweepBacklogSprints"
        >
          <calendar-days-icon class="h-4 w-4" />
          <span class="whitespace-nowrap">
            {{ sweeping ? 'Counting…' : 'Move backlog to no sprint' }}
          </span>
        </button>
        <span v-if="sweptNote" class="text-sm text-green-800">{{ sweptNote }}</span>
      </template>
      <div
        v-else
        class="h-9 px-2.5 border border-amber-500 bg-amber-50 rounded-md
               flex items-center gap-2 text-sm text-amber-900"
      >
        <template v-if="sweepCount">
          <span>
            Clear the sprint on {{ sweepCount }}
            Backlog item{{ sweepCount === 1 ? '' : 's' }}
            in dated sprints?
          </span>
          <button
            class="px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700
                   disabled:opacity-50"
            :disabled="sweeping"
            @click="sweepBacklogSprints"
          >
            {{ sweeping ? 'Moving…' : 'Yes, clear them' }}
          </button>
        </template>
        <!-- "on a dated sprint" rather than "on a sprint": with the
             undated buckets out of scope, a board where every Backlog item
             is parked in one of those is a board this button has nothing
             to do on, and saying so flatly would read as wrong to anybody
             looking at those items. -->
        <span v-else>No Backlog item is on a dated sprint.</span>
        <button
          class="text-xs underline hover:text-amber-700"
          :disabled="sweeping"
          @click="sweepCount = null"
        >
          {{ sweepCount ? 'Cancel' : 'Close' }}
        </button>
      </div>
    </div>

    <!-- What is selected, spelled out underneath. The control itself is one
         row tall so the filters stay aligned, which means more than a
         status or two scrolls out of sight inside it -- and a filter you
         cannot read is a filter you will forget you set. Each one here
         removes itself on click, the same as its chip does. -->
    <div
      v-if="anyFilterActive"
      class="mt-1.5 flex items-center gap-1 flex-wrap text-[11px]"
    >
      <span v-if="selectedStatuses.length" class="text-gray-500">
        Showing {{ selectedStatuses.length }} of {{ STATUSES.length }} statuses:
      </span>
      <button
        v-for="option in selectedStatuses"
        :key="option.value"
        class="rounded border border-gray-400 bg-white px-1.5 leading-[18px]
               text-dark-primary hover:bg-gray-100"
        :title="`Remove ${option.label}`"
        @click="removeStatus(option.value)"
      >
        {{ option.label }} ×
      </button>
      <!-- The single-valued filters, each reading as the word it is set to.
           Same chip as a status so the row reads as one list of what is
           narrowing the board, whichever control each one came from. -->
      <button
        v-for="filter in activeFilters"
        :key="filter.key"
        class="rounded border border-gray-400 bg-white px-1.5 leading-[18px]
               text-dark-primary hover:bg-gray-100"
        :title="`Remove ${filter.label}`"
        @click="filter.clear()"
      >
        {{ filter.label }} ×
      </button>
      <button
        class="text-gray-500 underline hover:text-gray-700"
        title="Remove every filter and show the whole backlog"
        @click="clearAllFilters"
      >
        Clear all
      </button>
    </div>

    <!-- Bulk bar. Only present when something is selected, so it never takes
         up room or invites a bulk action nobody asked for. -->
    <div
      v-if="selectedIds.size"
      class="flex flex-wrap items-center gap-2 mt-3 mb-3 p-2 rounded bg-gray-100
             border border-gray-300 text-sm"
    >
      <span>{{ selectedIds.size }} selected</span>
      <select v-model="bulkStatus" class="h-9 px-2 border border-gray-400 rounded-md">
        <option value="">Set status to…</option>
        <option v-for="s in STATUSES" :key="s.value" :value="s.value">{{ s.label }}</option>
      </select>
      <button
        class="px-2 py-1 rounded bg-accent-primary text-white hover:bg-accent-secondary
               disabled:opacity-50"
        :disabled="!bulkStatus || bulkRunning"
        @click="applyBulkStatus"
      >
        {{ bulkRunning ? `Applying ${bulkDone}/${selectedIds.size}…` : 'Apply' }}
      </button>
      <span v-if="bulkFailed" class="text-red-700">
        {{ bulkFailed }} failed — left unchanged
      </span>
      <button class="ml-auto text-xs text-gray-500 hover:underline" @click="clearSelection">
        Clear
      </button>
    </div>
    </div>

    <p v-if="loading" class="mt-6 text-gray-500">Loading…</p>
    <p v-else-if="error" class="mt-6 text-red-600">{{ error }}</p>

    <template v-else>
      <p v-if="!visible.length" class="mt-6 text-gray-500">
        Nothing matches these filters.
      </p>

      <div
        v-for="(group, index) in groups"
        :key="group.key"
        class="rounded-md border"
        :class="[
          // The first card butts up against the sticky block, whose padding
          // is now carrying that gap. Every card after it keeps its margin:
          // they are below the fold anyway, and a strip arriving from
          // further down the page is meant to travel.
          index === 0 ? '' : 'mt-4',
          dropTarget === group.key
            ? 'border-accent-primary bg-blue-50/40'
            : 'border-gray-400',
        ]"
        @dragover.prevent="dropTarget = group.key"
        @dragleave="dropTarget === group.key && (dropTarget = null)"
        @drop.prevent="dropOn(group)"
      >
        <!-- Amber, like the grouping header on My Expenses, which is the
             same element: a band across a table that folds the rows under it
             away. The whole strip is the control, not just the chevron --
             the header has nothing else to click, and a 14px target is a
             poor thing to aim at while talking. A button so it is reachable
             from the keyboard and announces its own state. -->
        <button
          type="button"
          :ref="index === 0 ? measureSprintStrip : undefined"
          class="w-full flex items-baseline gap-3 pl-2 pr-3 py-2 text-sm text-left
                 bg-amber-50 border-b border-amber-200 hover:bg-amber-100 sticky z-20"
          :style="{ top: (topStickyHeight + NAVBAR_HEIGHT) + 'px' }"
          :aria-expanded="!sprintCollapsed(group)"
          :title="sprintCollapsed(group) ? 'Expand this sprint' : 'Collapse this sprint'"
          @click="toggleSprint(group)"
        >
          <chevron-down-icon
            class="h-4 w-4 shrink-0 self-center text-gray-500"
            :class="sprintCollapsed(group) ? '-rotate-90' : ''"
          />
          <span class="text-xs text-gray-500">Sprint</span>
          <span class="font-semibold text-dark-primary">{{ group.label }}</span>
          <span class="text-xs text-gray-600">
            {{ group.rows.length }} row<span v-if="group.rows.length !== 1">s</span>
          </span>
        </button>

        <!-- table-fixed, because every sprint is its own table: left to
             size itself each one measured its own rows, so the Member and
             Status boxes sat a few pixels off between one sprint and the
             next. Fixed, the th widths below are the grid, and the only
             column that flexes is the title. -->
        <table v-if="!sprintCollapsed(group)" class="table-fixed w-full text-sm">
          <!-- The dark band My Expenses puts its column names on. It also
               settles what used to need a tiny uppercase treatment to avoid
               reading as the first story in the list: nothing on white is
               going to be confused with this. Below the sprint strip in the
               stacking order as well as on screen, so the strip passing over
               it as the next sprint arrives looks like one band replacing
               another. -->
          <thead
            class="bg-dark-primary text-white sticky z-10"
            :style="{ top: (topStickyHeight + NAVBAR_HEIGHT + sprintStripHeight) + 'px' }"
          >
            <tr class="bg-dark-primary text-white">
              <!-- The corner of the band, above the fold arrows on each
                   owner heading: a doubled chevron for "all of them at
                   once". Only with grouping on, since there are no people
                   sections to fold away without it.
                   It shares a centre line with the sprint chevron above and
                   the drag handles below; the owner chevron is the one that
                   stays indented, because it is a level down and a tree is
                   how that gets read. -->
              <th class="w-8 px-1 py-2">
                <button
                  v-if="groupByOwner"
                  type="button"
                  class="w-full flex items-center justify-center text-white hover:text-gray-300"
                  :aria-expanded="!allOwnersCollapsed(group)"
                  :title="allOwnersCollapsed(group)
                    ? 'Expand every owner in this sprint'
                    : 'Collapse every owner in this sprint'"
                  @click="toggleAllOwners(group)"
                >
                  <chevron-double-down-icon
                    class="h-4 w-4"
                    :class="allOwnersCollapsed(group) ? '-rotate-90' : ''"
                  />
                </button>
              </th>
              <th class="w-10 px-2 py-2 text-center">
                <input
                  type="checkbox"
                  class="w-[18px] h-[18px] align-middle cursor-pointer accent-accent-primary"
                  :checked="group.rows.length > 0 && groupFullySelected(group)"
                  :disabled="!group.rows.length"
                  @change="toggleGroup(group, $event.target.checked)"
                />
              </th>
              <th class="w-10 px-1 py-2 text-right">#</th>
              <th class="w-28 px-2 py-2"></th>
              <th class="text-left px-3 py-2">Story Name</th>
              <th class="w-44 text-left px-3 py-2">Member</th>
              <th class="w-40 text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <!-- One tbody per section: the split is a real division of the
               table, and it keeps each owner's heading inside the group it
               heads rather than floating between two bodies. With grouping
               off there is exactly one section holding every row, so the
               shape here does not change with the mode. -->
          <tbody v-for="section in group.sections" :key="section.key">
            <tr v-if="groupByOwner">
              <!-- Spanning the table, so the heading reads as a heading
                   rather than as a row with six empty cells after it. -->
              <td colspan="7" class="p-0 border-t border-gray-300">
                <!-- Grey rather than the sprint's amber: it is the second
                     level of the same idea, and two ambers stacked read as
                     one band with a line through it. Set in past the handle
                     and checkbox columns so it reads as something filed
                     under the sprint rather than as another sprint. The
                     indent does that on its own -- a rule down the left edge
                     as well just looked like a stray line in the margin. -->
                <button
                  type="button"
                  class="w-full flex items-center gap-2 pl-10 pr-3 py-1.5 text-left
                         bg-gray-100 hover:bg-gray-200"
                  :aria-expanded="!ownerCollapsed(group, section)"
                  :title="ownerCollapsed(group, section)
                    ? `Expand ${section.label}`
                    : `Collapse ${section.label}`"
                  @click="toggleOwner(group, section)"
                >
                  <chevron-down-icon
                    class="h-3.5 w-3.5 shrink-0 text-gray-400"
                    :class="ownerCollapsed(group, section) ? '-rotate-90' : ''"
                  />
                  <span
                    class="text-sm font-semibold"
                    :class="section.key === OWNER_NONE
                      ? 'text-gray-500 italic'
                      : 'text-dark-primary'"
                  >{{ section.label }}</span>
                  <span class="text-xs text-gray-600">
                    {{ section.rows.length }} row<span v-if="section.rows.length !== 1">s</span>
                  </span>
                </button>
              </td>
            </tr>
            <!-- gray-300 for the divider, not gray-200: the rows carry no
                 background of their own, so what shows through them is the
                 page's own bg-gray-200, and a gray-200 rule drawn on that
                 was invisible. One step darker is still faint and is
                 actually there. -->
            <tr
              v-for="row in sectionRows(group, section)"
              :key="row.id"
              class="border-t border-gray-300 hover:bg-gray-50"
              :class="[
                draggingIds.includes(row.id) ? 'opacity-40' : '',
                rowTint(row),
                dropRowId === row.id ? 'border-t-2 border-t-accent-primary' : '',
              ]"
              @dragover.prevent.stop="rowDragOver(row)"
              @drop.prevent.stop="dropOnRow(row)"
            >
              <!-- Bigger and darker than it was: at text-sm in gray-300 the
                   one control on the row that has to be found before it can
                   be used was the faintest thing on it.
                   cursor-grab in both modes, because dragging works in both
                   -- grouped by owner it reorders within the owner's own
                   section, which is what the title says. -->
              <td
                class="px-1 py-1.5 align-top text-center text-base leading-5
                       text-gray-500 hover:text-dark-primary cursor-grab"
                :title="groupByOwner
                  ? 'Drag to reorder within this owner, or onto another sprint'
                  : 'Drag to reorder, or onto another sprint'"
                draggable="true"
                @dragstart="startDrag(row)"
                @dragend="endDrag"
              >⠿</td>

              <td class="px-2 py-1.5 align-top text-center">
                <input
                  type="checkbox"
                  class="w-[18px] h-[18px] align-middle cursor-pointer accent-accent-primary"
                  :checked="selectedIds.has(row.id)"
                  @change="toggleRow(row, $event.target.checked)"
                />
              </td>

              <!-- Deliberately quiet: it is a pointer for the room, not an
                   identity. The work item's own id used to sit beside it and
                   was two columns of numbers saying different things; the id
                   is still on the row's edit form, which is where anyone who
                   needs it is going anyway. -->
              <!-- leading-5 so it lands on the title's line. Top-aligned
                   text-xs gets a 16px line box against the title's 20px,
                   which left the number sitting a couple of pixels high --
                   small, but it is a column of them. -->
              <td class="px-1 py-1.5 align-top text-right text-xs leading-5 text-gray-500 tabular-nums">
                {{ rowNumbers.get(row.id) }}
              </td>

              <td class="px-2 py-1.5 align-top text-center">
                <!-- nowrap as well as a column wide enough to hold it: with
                     table-fixed the column will not grow to fit "milestone",
                     and a kind broken over two lines doubled the height of
                     every row it appeared in. -->
                <span
                  class="text-xs px-1.5 py-0.5 rounded whitespace-nowrap"
                  :class="kindClass(row.kind)"
                >{{ row.kind }}</span>
                <!-- Under the word it qualifies: the date is what being a
                     milestone means, not a fact about the story's name. -->
                <span
                  v-if="row.kind === 'milestone' && row.target_date"
                  class="block text-[11px] text-amber-900 tabular-nums mt-0.5 whitespace-nowrap"
                >{{ row.target_date }}</span>
              </td>

              <!-- Two gestures in one cell. The words open the full form, as
                   they always have; the space beside them renames in place.
                   Rewording a story is the edit that happens mid-meeting and
                   it did not deserve a modal -- everything else on a work
                   item still does. -->
              <td
                class="px-3 py-1.5 align-top break-words"
                :title="editingTitle === row.id
                  ? ''
                  : 'Double-click here to rename'"
                @dblclick="startEditTitle(row)"
              >
                <input
                  v-if="editingTitle === row.id"
                  ref="titleInput"
                  v-model="titleDraft"
                  type="text"
                  class="w-full h-7 px-2 border border-gray-400 rounded-md"
                  :disabled="saving === row.id"
                  @keyup.enter="commitTitle(row)"
                  @keydown.esc="cancelEditTitle"
                  @blur="commitTitle(row)"
                />
                <template v-else>
                  <!-- .stop so a double-click aimed at the words is not also
                       a double-click on the cell: the form is already
                       opening, and an edit box left behind it would be
                       waiting with the cursor in it on the way back. -->
                  <span
                    class="cursor-pointer hover:text-accent-primary hover:underline"
                    title="Click to open"
                    @click="openEdit(row)"
                    @dblclick.stop
                  >{{ row.title }}</span>
                  <span
                    v-if="row.is_blocked"
                    class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 ml-1"
                  >blocked</span>
                  <span
                    v-if="row.is_flagged"
                    class="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 ml-1"
                  >flagged</span>
                </template>
              </td>

              <td
                class="px-3 py-1.5 align-top"
                :title="editingMember === row.id ? '' : 'Double-click to reassign'"
                @dblclick="startEditMember(row)"
              >
                <select
                  v-if="editingMember === row.id"
                  ref="memberSelect"
                  :value="row.assignee || ''"
                  class="w-full h-7 px-2 border border-gray-400 rounded-md text-sm"
                  :disabled="saving === row.id"
                  @change="changeMember(row, $event.target.value)"
                  @blur="editingMember = null"
                  @keyup.esc="editingMember = null"
                >
                  <option value="">— unassigned —</option>
                  <option
                    v-for="u in memberChoices(row)"
                    :key="u.id"
                    :value="u.id"
                  >{{ u.label }}</option>
                </select>

                <span
                  v-else-if="row.assignee_display"
                  class="inline-flex items-center gap-1 cursor-pointer"
                >
                  <span
                    class="w-4 h-4 rounded-full text-[10px] text-white flex items-center justify-center shrink-0"
                    :class="avatarClass(row.assignee_display)"
                  >{{ row.assignee_display.charAt(0) }}</span>
                  {{ row.assignee_display }}
                </span>
                <span v-else class="text-gray-400 cursor-pointer">—</span>
              </td>

              <td class="px-3 py-1.5 align-top">
                <select
                  :value="row.status"
                  class="w-full h-7 px-1 border border-gray-400 rounded-md text-xs text-center"
                  :class="statusChip(row.status)"
                  :disabled="saving === row.id"
                  @change="patch(row, { status: $event.target.value })"
                >
                  <option v-for="s in STATUSES" :key="s.value" :value="s.value">
                    {{ s.label }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paging is a button, not an infinite scroll: rows are dragged to
           reorder here, and a list that grows under the cursor mid-drag is
           a good way to drop something into the wrong sprint. -->
      <div v-if="hasMore" class="mt-4 flex items-center gap-3">
        <button
          class="px-3 py-1.5 text-sm rounded-md border border-gray-400
                 hover:bg-gray-100 disabled:opacity-50"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? 'Loading…' : 'Load more' }}
        </button>
        <span class="text-sm text-gray-500">
          {{ items.length }} of {{ matching }} loaded
        </span>
      </div>
    </template>

    <work-item-edit-modal
      :item="editing"
      :create="creating"
      :sprints="sprints"
      :users="assignableUsers"
      :departments="departments"
      :parent-options="parentOptions"
      @close="closeModal"
      @saved="onSaved"
      @created="onCreated"
      @archived="onArchived"
      @attachments-changed="onAttachmentsChanged"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

import {
  ArrowPathIcon, ArrowUturnLeftIcon, BoltIcon, CalendarDaysIcon,
  ChevronDoubleDownIcon, ChevronDownIcon, FlagIcon, InformationCircleIcon,
  PlusIcon, UserGroupIcon, UsersIcon,
} from '@heroicons/vue/20/solid'

import WorkItemEditModal from '@/components/back-office/WorkItemEditModal'
import { useStickyOffset } from '@/composables/useStickyOffset'
import {
  clearBacklogSprints,
  getDepartments,
  getSprints,
  getWorkItems,
  updateWorkItem,
} from '@/services/BackOffice'
import { getAllUsersWithGroups } from '@/services/User'
import { dragSet, planMoveToFront, planReorder } from '@/utils/backlogOrdering'
import {
  WORK_ITEM_KINDS as KINDS,
  WORK_ITEM_STATUSES as STATUSES,
  workItemKindClass as kindClass,
  workItemStatusChip as statusChip,
} from '@/utils/workItemOptions'

const AVATARS = ['bg-teal-600', 'bg-purple-600', 'bg-sky-600', 'bg-rose-600', 'bg-amber-600']

// --- the three bands that stay on screen ---------------------------------
//
// The app's own navbar is fixed at this height, and every sticky thing on
// the expenses screens is offset by it. Named here rather than spelled 56
// twice in the template, which is how it is written over there.
const NAVBAR_HEIGHT = 56

// The filter block, measured live: selecting rows adds the bulk bar to it,
// and a narrow window wraps the filter row onto a second line. Both move
// everything below it down.
const topStickyRef = ref(null)
const topStickyHeight = useStickyOffset(topStickyRef)

// Where the column headers come to rest: under the sprint strip, which is
// itself under the filters. Measured off the first strip rather than
// assumed, because a long sprint name wraps -- and measured by hand rather
// than with useStickyOffset, which reads its element once on mount and there
// are no sprints on screen until the first load lands.
const sprintStripHeight = ref(0)
let sprintStripObserver = null

function measureSprintStrip(el) {
  if (sprintStripObserver) {
    sprintStripObserver.disconnect()
    sprintStripObserver = null
  }
  // Vue calls a function ref with null as the element goes away.
  if (!el) return
  const read = () => {
    sprintStripHeight.value = Math.round(el.getBoundingClientRect().height)
  }
  read()
  if (typeof ResizeObserver !== 'undefined') {
    sprintStripObserver = new ResizeObserver(read)
    sprintStripObserver.observe(el)
  }
}

// Who can be given work from this board. The list behind the dropdowns is
// every active Aerocle account -- about a hundred people -- and three of
// them run this backlog.
//
// Usernames rather than ids because two of the three have a second, older
// account that owns nothing (patrick-alvarez beside patrick_alvarez,
// namron_chui beside norman_chui), and a list of bare numbers would not
// show which one was meant.
//
// A rule about what to offer next, not about what is already true: 1,098
// live items belong to someone who is not on this list, and they keep
// their owner. See memberChoices(), which always carries the current one.
// Nothing here is enforced by the API -- it is a shorter menu, not a
// permission -- so this is the only place to change it.
const ASSIGNABLE_USERNAMES = ['gordon_wong', 'patrick_alvarez', 'norman_chui']

// Who the Owner filter names. The question it answers is "whose work am I
// looking at", and on this board the answer is nearly always one of four
// people -- they hold 2,241 of the 2,298 assigned items -- so offering a
// hundred accounts that own nothing made it a scrolling exercise.
//
// Dylan belongs here and not above: he owns more of this backlog than
// anyone, and none of it is work he is still being given. Dylan has three
// accounts and only this one owns anything, which is the other reason these
// lists are written as usernames.
const FILTERABLE_USERNAMES = [...ASSIGNABLE_USERNAMES, 'dylan_marvel']
const NO_SPRINT = '__none__'

const store = useStore()
const token = () => store.getters['auth/showToken']

const items = ref([])
const sprints = ref([])

// Fibonacci, matching how the team already sizes things, plus 1: the
// sequence skips it, and "the sprint either side of this one" is the window
// people reach for most after the current one on its own. 0 is "just the
// current sprint" -- the default, because the usual question is what is
// happening now, not what the whole year looks like.
const SPRINT_WINDOW = [0, 1, 2, 3, 5, 8, 13, 21]
// Read off the list rather than spelled 21, so widening the window above
// widens "as far forward as the board goes" with it.
const SPRINT_WINDOW_MAX = Math.max(...SPRINT_WINDOW)
// "As far as the board goes", in whichever direction it is set on. A number
// rather than a flag because every reader of these two refs does arithmetic
// with them, and the window is already clamped to the ends of the sprint
// list -- so a number past the end simply means the end, and no reader has
// to learn about this one. MAX_SAFE_INTEGER rather than Infinity: the page
// state is persisted as JSON, which writes Infinity out as null and would
// restore the window as nothing at all.
const SPRINT_WINDOW_ALL = Number.MAX_SAFE_INTEGER
// The two the shortcuts offer. Back two covers "what did we just finish",
// forward eight a quarter of planning -- the two windows people asked for
// by name, which is why they get a button and the rest stay in the dropdown.
const SPRINTS_BACK_SHORTCUT = 2
const SPRINTS_FORWARD_SHORTCUT = 8
const sprintsBack = ref(0)
const sprintsForward = ref(0)
// Off by default: unscheduled work is a different question from what the
// team is doing, and it is the larger pile.
const showNoSprint = ref(false)

// --- how the rows are arranged, as opposed to which rows they are --------
//
// None of these three ask the server for anything. They are kept apart from
// the filters for that reason: changing one rearranges what is already on
// screen, and nothing reloads.
const groupByOwner = ref(false)

// Collapsed rather than expanded state is stored, so a sprint that did not
// exist when you last used the board arrives open. Arrays rather than Sets
// because this is persisted as JSON, and a Set serialises to {}.
const collapsedSprints = ref([])
const collapsedOwners = ref([])

// Earliest first, so the board reads forward in time. The API orders
// sprints newest-first (Sprint.Meta.ordering) and other screens rely on
// that, so this is sorted here rather than by changing the model.
const sprintsAscending = computed(() => [...sprints.value].sort((a, b) => {
  const left = a.start_date || ''
  const right = b.start_date || ''
  if (left && right && left !== right) return left < right ? -1 : 1
  // A sprint with no dates cannot be placed in time; keep it after the ones
  // that can be, in a stable order rather than wherever sort leaves it.
  if (!left && right) return 1
  if (left && !right) return -1
  return String(a.name).localeCompare(String(b.name))
}))

// Today as the server writes dates: local, not UTC. toISOString() would roll
// over to tomorrow from early evening Central onwards, which on the last day
// of a sprint silently anchors the board on the next one.
function localToday() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

// The sprint containing today. Sprints can have gaps between them, so when
// today falls in one, the next one to start is "current" -- being shown the
// sprint about to begin is more useful than being shown the one that ended.
const currentSprintIndex = computed(() => {
  const list = sprintsAscending.value
  if (!list.length) return -1
  const today = localToday()
  const containing = list.findIndex(
    sp => sp.start_date && sp.end_date && sp.start_date <= today && today <= sp.end_date,
  )
  if (containing !== -1) return containing
  const upcoming = list.findIndex(sp => sp.start_date && sp.start_date > today)
  if (upcoming !== -1) return upcoming
  // Everything is in the past: anchor on the most recent one that has dates.
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].start_date) return i
  }
  return -1
})

// Which sprint ids survive the two window filters. Null means "no window to
// apply" -- with no dated sprints at all, hiding everything would be worse
// than showing everything.
const sprintsInWindow = computed(() => {
  const anchor = currentSprintIndex.value
  if (anchor === -1) return null
  const list = sprintsAscending.value
  const from = Math.max(0, anchor - sprintsBack.value)
  const to = Math.min(list.length - 1, anchor + sprintsForward.value)
  return new Set(list.slice(from, to + 1).map(sp => String(sp.id)))
})
const users = ref([])
const loading = ref(true)
const saving = ref(null)
const draggingIds = ref([])
const dropTarget = ref(null)
const dropRowId = ref(null)
const editingMember = ref(null)
const memberSelect = ref(null)
const editingTitle = ref(null)
// The box holds a draft rather than writing through to the row: a title
// half-typed is not a title, and Escape has to leave the old one standing.
const titleDraft = ref('')
const titleInput = ref(null)
const editing = ref(null)
// The same modal does both jobs, so which job it is doing has to be said
// out loud: a brand new item has no id for the form to patch.
const creating = ref(false)
const departments = ref([])
const parentOptions = ref([])
const error = ref('')

const selectedIds = ref(new Set())
const bulkStatus = ref('')
const bulkRunning = ref(false)
const bulkDone = ref(0)
const bulkFailed = ref(0)

// Clearing the sprint off everything still at Backlog. Three pieces of
// state because it is a three-step button: ask the server how many, show
// that number, then do it. `sweepCount` of null means the question has not
// been asked, which is also what Cancel goes back to.
const sweeping = ref(false)
const sweepCount = ref(null)
const sweptNote = ref('')

const search = ref('')
const status = ref([])
const kind = ref('')
const assignee = ref('')
const overlay = ref('')

// One sprint's work is tens of rows (the current one holds 30), so a window
// is normally a single page. The cap is what stops a window opened wide
// across the 234 sprints of history from arriving all at once.
const PAGE_SIZE = 250
const matching = ref(0)        // what the server says the filters match
const pagesLoaded = ref(0)
const hasMore = ref(false)
const loadingMore = ref(false)

// --- remembering how you left the page ------------------------------------
//
// sessionStorage, not localStorage: the choices should survive navigating
// away and back, not outlive the session on a shared machine. Keyed by user
// id as well, so two accounts on one browser never inherit each other's view.
//
// Versioned because the saved shape is this component's business. Renaming a
// filter later would otherwise restore a value into a ref that no longer
// means the same thing; bumping the version simply forgets once.
// v1 saved `status` as a single string; v2 saved `assignee` as a display
// name, which became a user id when the owner filter moved to the API.
const PAGE_STATE_VERSION = 'v3'

// Every ref here is saved and restored together. Page state added later --
// a collapsed sprint, a chosen grouping -- joins by adding one line.
// Deliberately excluded: selection, bulk progress and the open modal, which
// are about what you are doing right now rather than how you like the page.
const PERSISTED = {
  search, status, kind, assignee, overlay,
  sprintsBack, sprintsForward, showNoSprint,
  groupByOwner, collapsedSprints, collapsedOwners,
}

function pageStateKey() {
  let userId = 'anon'
  try {
    const user = JSON.parse(window.localStorage.getItem('user') || 'null')
    if (user && user.id) userId = user.id
  } catch (e) {
    // Unreadable or absent: fall back to the shared key rather than failing.
  }
  return `backlogBoard.${PAGE_STATE_VERSION}.${userId}`
}

function restorePageState() {
  try {
    const raw = window.sessionStorage.getItem(pageStateKey())
    if (!raw) return
    const saved = JSON.parse(raw)
    for (const [name, target] of Object.entries(PERSISTED)) {
      // Only keys actually saved: a ref whose key is missing keeps its
      // default, which is what makes adding a new one to PERSISTED safe.
      if (saved[name] !== undefined) target.value = saved[name]
    }
  } catch (e) {
    // A corrupt or blocked store must not take the page down with it.
  }
}

function savePageState() {
  try {
    const out = {}
    for (const [name, target] of Object.entries(PERSISTED)) out[name] = target.value
    window.sessionStorage.setItem(pageStateKey(), JSON.stringify(out))
  } catch (e) {
    // Private windows throw on write. Losing the preference is acceptable.
  }
}

// Restored here rather than in onMounted: these are server-side filters, so
// they have to hold their saved values before the first load().
restorePageState()
watch(Object.values(PERSISTED), savePageState)

// Every filter is a server filter now, so changing one means asking again.
// Search is not here: it reloads on Enter, so that typing a title does not
// fire a request per keystroke. The sprint window is watched through its
// two controls rather than through sprintsInWindow, which also changes when
// the sprint list first arrives -- and that load is already on its way.
watch(
  [status, assignee, overlay, kind, sprintsBack, sprintsForward, showNoSprint],
  () => { load() },
  // Insurance, not the fix: `status` is copied where it is set, so its
  // identity does change. But it is the one source here that is an array,
  // it is filled by a shared component that mutates in place, and the
  // failure is silent and wrong rather than loud -- a board showing rows
  // the filter above it excludes. Deep, a mutation that slipped through
  // still reloads.
  { deep: true },
)

// The work that is actually on. It is how most people arrive at this board,
// and reaching it meant two trips through the multiselect every time, so it
// gets a button. Values rather than labels: these read as "Not Started",
// "Next" and "In Progress" on screen.
//
// Next belongs here. It is a promise about this week as much as in_progress
// is, and a shortcut called Active work that hid the thing somebody just
// said they were picking up next would be read as a bug.
const ACTIVE_STATUSES = ['planned', 'next', 'in_progress']

// Lit only when the filter is exactly those two. Anything else -- one of
// them, or both plus a third -- is a filter the person built themselves,
// and the shortcut should not claim credit for it.
const activeOnly = computed(
  () => status.value.length === ACTIVE_STATUSES.length
    && ACTIVE_STATUSES.every(value => status.value.includes(value)),
)

// A toggle, so the button that got you here can get you back: clicking it
// while it is lit clears the filter rather than reapplying it.
function toggleActiveOnly() {
  status.value = activeOnly.value ? [] : [...ACTIVE_STATUSES]
}

// The chosen statuses, in the order the filter lists them rather than the
// order they were clicked, so the summary row reads the same every time.
const selectedStatuses = computed(
  () => STATUSES.filter(option => status.value.includes(option.value)),
)

// Removing from the summary row drives the same ref the control is bound
// to, and the control watches its default-value, so the two cannot drift.
function removeStatus(value) {
  status.value = status.value.filter(chosen => chosen !== value)
}

// --- the other shortcuts --------------------------------------------------
//
// Same idea as Active work: each one sets the control it sits above, rather
// than being a filter of its own. All of them toggle, so the button that got
// you somewhere gets you back.

// Me, by username. The auth store keeps the signed-in username but no id,
// and the owner filter asks the API by id, so the two are matched through
// the user list the board already loads. Null until that list arrives, or
// if the signed-in account is not in it -- the My button hides itself
// rather than sitting there setting the filter to nothing.
const myUserId = computed(() => {
  const username = store.getters['auth/showUsername']
  if (!username) return null
  const mine = users.value.find(u => u.username === username)
  return mine ? mine.id : null
})

const ownerIsMe = computed(
  () => myUserId.value !== null && assignee.value === myUserId.value,
)

function showMyWork() {
  if (myUserId.value === null) return
  assignee.value = ownerIsMe.value ? '' : myUserId.value
}

function toggleSprintsBack() {
  sprintsBack.value = sprintsBack.value === SPRINTS_BACK_SHORTCUT
    ? 0
    : SPRINTS_BACK_SHORTCUT
}

function toggleSprintsForward() {
  sprintsForward.value = sprintsForward.value === SPRINTS_FORWARD_SHORTCUT
    ? 0
    : SPRINTS_FORWARD_SHORTCUT
}

// The planning sweep: wide enough to hold what the team just finished and
// what it has committed to next, with nothing filtered out of it and the
// rows split by owner so each person's share reads as a block. It is the
// view a planning call wants, and reaching it by hand was seven controls.
//
// Two back and three forward rather than the window shortcuts' own numbers:
// those answer "show me a bit more either side", this one is a fixed view
// somebody asked for by name, and it should not move when they are retuned.
const TEAM_VIEW_BACK = 2
const TEAM_VIEW_FORWARD = 3

// Lit only when the board is exactly this view, the same rule Active work
// follows: a window that happens to be two and three while a type filter is
// also set is a view the person built, and the button should not claim it.
//
// Search is the one thing it sets without testing -- it lives outside the
// filter watcher, and its chip is always on screen to say so.
const teamView = computed(
  () => sprintsBack.value === TEAM_VIEW_BACK
    && sprintsForward.value === TEAM_VIEW_FORWARD
    && !status.value.length
    && assignee.value === ''
    && kind.value === ''
    && overlay.value === ''
    && !showNoSprint.value
    && groupByOwner.value,
)

function showTeamView() {
  if (teamView.value) {
    resetFilters()
    // Grouping is a view control, so Reset leaves it alone -- but this
    // button turned it on, and the button that got you here gets you back.
    groupByOwner.value = false
    return
  }
  status.value = []
  kind.value = ''
  assignee.value = ''
  overlay.value = ''
  sprintsBack.value = TEAM_VIEW_BACK
  sprintsForward.value = TEAM_VIEW_FORWARD
  // Work with no sprint sits outside every window and is the larger pile;
  // sweeping it in would bury the six sprints this view is about.
  showNoSprint.value = false
  groupByOwner.value = true
  if (search.value) {
    search.value = ''
    load()
  }
}

// Every milestone between now and as far ahead as the window reaches.
// Statuses are cleared as well as the type being set: a milestone sitting
// in Backlog is still a date somebody is holding us to, and a shortcut that
// hid it because Active work happened to be on would read as broken.
const upcomingMilestones = computed(
  () => kind.value === 'milestone'
    && sprintsForward.value === SPRINT_WINDOW_MAX
    && sprintsBack.value === 0,
)

function showUpcomingMilestones() {
  if (upcomingMilestones.value) {
    resetFilters()
    return
  }
  status.value = []
  kind.value = 'milestone'
  assignee.value = ''
  overlay.value = ''
  sprintsBack.value = 0
  sprintsForward.value = SPRINT_WINDOW_MAX
  showNoSprint.value = false
  if (search.value) {
    search.value = ''
    load()
  }
}

// Back to how the board opens: the work that is on, everyone's, this sprint
// only, any type. Search and the blocked/flagged overlay go too -- a reset
// that left a search term quietly narrowing the board is the kind of thing
// people file a bug about.
function resetFilters() {
  const hadSearch = Boolean(search.value)
  search.value = ''
  status.value = [...ACTIVE_STATUSES]
  kind.value = ''
  assignee.value = ''
  overlay.value = ''
  sprintsBack.value = 0
  sprintsForward.value = 0
  showNoSprint.value = false
  // Search is the one filter outside the reload watcher, so clearing it on
  // its own would leave the board showing a search that is no longer set.
  if (hadSearch) load()
}

// --- what is currently narrowing the board -------------------------------
//
// Every active filter, spelled out under the controls. The statuses have
// their own run of chips above this one because there can be several; these
// are the single-valued ones, each with the word it is set to and a click
// to drop it.
//
// The point is that a filter you cannot see is a filter you forget you set,
// and the controls are a row of dropdowns whose chosen value is easy to
// miss at a glance -- most of a support question about this board came down
// to a sprint window somebody had left open.
const activeFilters = computed(() => {
  const out = []
  const sprintWord = n => (n === 1 ? 'sprint' : 'sprints')

  const term = search.value.trim()
  if (term) {
    out.push({
      key: 'search',
      label: `Search: \u201c${term}\u201d`,
      clear: () => { search.value = ''; load() },
    })
  }
  if (assignee.value) {
    const known = users.value.find(u => u.id === assignee.value)
    out.push({
      key: 'owner',
      label: `Owner: ${(known && known.label) || `User ${assignee.value}`}`,
      clear: () => { assignee.value = '' },
    })
  }
  if (kind.value) {
    const known = KINDS.find(k => k.value === kind.value)
    out.push({
      key: 'kind',
      label: `Type: ${(known && known.label) || kind.value}`,
      clear: () => { kind.value = '' },
    })
  }
  if (overlay.value) {
    out.push({
      key: 'overlay',
      label: overlay.value === 'blocked' ? 'Blocked only' : 'Flagged only',
      clear: () => { overlay.value = '' },
    })
  }
  if (sprintsBack.value) {
    out.push({
      key: 'back',
      label: sprintsBack.value === SPRINT_WINDOW_ALL
        ? 'Back to the earliest sprint'
        : `${sprintsBack.value} ${sprintWord(sprintsBack.value)} back`,
      clear: () => { sprintsBack.value = 0 },
    })
  }
  if (sprintsForward.value) {
    out.push({
      key: 'forward',
      label: sprintsForward.value === SPRINT_WINDOW_ALL
        ? 'Forward to the latest sprint'
        : `${sprintsForward.value} ${sprintWord(sprintsForward.value)} forward`,
      clear: () => { sprintsForward.value = 0 },
    })
  }
  if (showNoSprint.value) {
    out.push({
      key: 'nosprint',
      label: 'Including work with no sprint',
      clear: () => { showNoSprint.value = false },
    })
  }
  return out
})

// Whether the row is worth drawing at all.
const anyFilterActive = computed(
  () => selectedStatuses.value.length > 0 || activeFilters.value.length > 0,
)

// Empties every filter, as opposed to Reset, which sets the Active work
// view. Both are offered because they answer different questions: "show me
// everything" and "put it back how it was".
function clearAllFilters() {
  const hadSearch = Boolean(search.value)
  search.value = ''
  status.value = []
  kind.value = ''
  assignee.value = ''
  overlay.value = ''
  sprintsBack.value = 0
  sprintsForward.value = 0
  showNoSprint.value = false
  if (hadSearch) load()
}

// The owner list used to be derived from the loaded rows. It now comes from
// the user list, because the filter asks the API by id and a list built from
// a single page would hide the people whose work is on the next one.

// Status, owner and the blocked/flagged overlay are now applied by the API
// (see filterParams), so what came back is already what should be shown.
// Kept as a named value because the template and the grouping both read it,
// and because the sprint window below still narrows the buckets.
const visible = computed(() => items.value)

// The one section a sprint has when it is not split by owner. Named so the
// collapse keys below have nothing to collide with, and so the rendering
// can stay one shape rather than branching on whether grouping is on.
const SECTION_ALL = '__all__'
const OWNER_NONE = '__unassigned__'

// Splits a sprint's rows by owner, keeping each owner's rows in the order
// they already had. Called per sprint rather than over the whole board,
// because the question in a planning call is "what does Priya have in this
// sprint", not "what does Priya have across the quarter".
function ownerSections(rows) {
  const buckets = new Map()
  for (const row of rows) {
    const key = row.assignee ? String(row.assignee) : OWNER_NONE
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        label: row.assignee_display || 'Unassigned',
        rows: [],
      })
    }
    buckets.get(key).rows.push(row)
  }
  const out = [...buckets.values()]
  // By name, with unassigned last: it is a hole in the plan rather than a
  // person, and sitting between two names is how it gets read as one.
  out.sort((a, b) => {
    if (a.key === OWNER_NONE) return 1
    if (b.key === OWNER_NONE) return -1
    return a.label.localeCompare(b.label)
  })
  return out
}

const groups = computed(() => {
  const buckets = new Map()
  for (const row of visible.value) {
    const key = row.sprint ? String(row.sprint) : NO_SPRINT
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        sprintId: row.sprint || null,
        label: row.sprint_name || 'No sprint',
        rows: [],
      })
    }
    buckets.get(key).rows.push(row)
  }

  // Only sprints with rows in them become sections. The sprint window used
  // to be drawn in full, every week in it getting a heading whether the
  // filter had left it anything or not -- which with a status or a type set
  // and a wide window meant a screen of headings reading "0 rows", the
  // board describing its own sprint list instead of answering the filter.
  //
  // What that cost: an empty section was also a drop target, so a sprint
  // the filter had emptied could be dragged back into. It cannot now. The
  // Sprint field on the item's own form still moves it anywhere, and
  // clearing the filter brings the week back as a target -- whereas there
  // was no way at all to get rid of the empty headings.
  const out = [...buckets.values()]
  for (const group of out) {
    // Rank alone decides the order. Sorting by owner first would make it
    // impossible to drop a row between two rows owned by other people,
    // which is the whole point of dragging to reorder.
    group.rows.sort((a, b) => a.rank - b.rank || a.id - b.id)
    // Owner grouping is a second reading of the same rows, not a different
    // order: group.rows stays rank-sorted because drag-and-drop, the group
    // checkbox and both counts all read it. Only the rendering splits.
    group.sections = groupByOwner.value
      ? ownerSections(group.rows)
      : [{ key: SECTION_ALL, label: '', rows: group.rows }]
  }

  const window = sprintsInWindow.value
  let kept = window === null
    ? out
    // 'No sprint' is never windowed out by the sprint range -- it sits
    // outside time, so a window either side of the current sprint says
    // nothing about it. Its own toggle below decides whether it shows.
    : out.filter(group => group.key === NO_SPRINT || window.has(group.key))

  if (!showNoSprint.value) {
    kept = kept.filter(group => group.key !== NO_SPRINT)
  }

  const order = new Map(sprintsAscending.value.map((s, i) => [String(s.id), i]))
  kept.sort((a, b) => {
    if (a.key === NO_SPRINT) return 1
    if (b.key === NO_SPRINT) return -1
    return (order.get(a.key) ?? 1e9) - (order.get(b.key) ?? 1e9)
  })
  return kept
})

const shownCount = computed(
  () => groups.value.reduce((total, group) => total + group.rows.length, 0),
)

// --- collapsing ----------------------------------------------------------
//
// Reassigns the array rather than pushing into it, so the template
// re-evaluates and the watcher that saves page state actually fires.
function toggleKey(list, key) {
  return list.includes(key) ? list.filter(k => k !== key) : [...list, key]
}

// Hidden rather than dropped: the heading stays put, so a folded-away owner
// reads as a line to click rather than as an owner who has nothing on.
function sectionRows(group, section) {
  if (groupByOwner.value && ownerCollapsed(group, section)) return []
  return section.rows
}

function sprintCollapsed(group) {
  return collapsedSprints.value.includes(group.key)
}

function toggleSprint(group) {
  collapsedSprints.value = toggleKey(collapsedSprints.value, group.key)
}

// Qualified by the sprint: the same person owns work in several sprints, and
// folding them away in one is no statement about the others.
function ownerCollapseKey(group, section) {
  return `${group.key}::${section.key}`
}

function ownerCollapsed(group, section) {
  return collapsedOwners.value.includes(ownerCollapseKey(group, section))
}

function toggleOwner(group, section) {
  collapsedOwners.value = toggleKey(
    collapsedOwners.value, ownerCollapseKey(group, section),
  )
}

function ownerKeys(group) {
  return group.sections.map(section => ownerCollapseKey(group, section))
}

// Every one of them, or there is still something to fold. Which makes the
// button below a toggle rather than two buttons: the state it reports is
// the state one more click would leave.
function allOwnersCollapsed(group) {
  const keys = ownerKeys(group)
  return keys.length > 0 && keys.every(key => collapsedOwners.value.includes(key))
}

// Scoped to the one sprint, like every other collapse on this board: the
// same person owns work in several, and folding them away here says nothing
// about the others.
function toggleAllOwners(group) {
  const keys = ownerKeys(group)
  if (!keys.length) return
  if (allOwnersCollapsed(group)) {
    const dropping = new Set(keys)
    collapsedOwners.value = collapsedOwners.value.filter(key => !dropping.has(key))
    return
  }
  // A Set so a sprint folded twice does not store its keys twice.
  const next = new Set(collapsedOwners.value)
  for (const key of keys) next.add(key)
  collapsedOwners.value = [...next]
}

// --- the numbers in the first column -------------------------------------
//
// Cosmetic on purpose. The number exists so that someone can say "row 14"
// in a planning call and have everyone land on the same line.
//
// Which is why it counts the filtered set rather than the fold: 1 at the top
// row to n at the bottom, counting every row these filters match, whether or
// not the sprint or the owner it sits under is folded away at the time.
// Renumbering as things were folded meant two people on identical filters
// disagreed about which row was 14, which is the one thing the number is for.
//
// Collapsing is the only arrangement it ignores. The order is still the order
// on screen -- including the owner sections, so the column reads straight
// down the page rather than jumping about inside a sprint.
const rowNumbers = computed(() => {
  const out = new Map()
  let n = 0
  for (const group of groups.value) {
    for (const section of group.sections) {
      for (const row of section.rows) {
        n += 1
        out.set(row.id, n)
      }
    }
  }
  return out
})

// The four, and then anyone else who owns something currently on the board.
// Thirteen people hold 57 items between them, and a flat four would put
// those beyond reach of the filter entirely; naming someone only while
// their work is in view keeps the list short without hiding anything that
// is on screen to be asked about.
const filterOwners = computed(() => {
  const named = users.value.filter(u => FILTERABLE_USERNAMES.includes(u.username))
  const namedIds = new Set(named.map(u => u.id))
  const others = new Map()

  const add = (id, fallback) => {
    if (!id || namedIds.has(id) || others.has(id)) return
    const known = users.value.find(u => u.id === id)
    others.set(id, { id, label: (known && known.label) || fallback || `User ${id}` })
  }

  for (const row of items.value) add(row.assignee, row.assignee_display)
  // A filter restored from a previous visit has to stay listed even if its
  // owner has nothing in this window, or the control would read "All
  // owners" while the board sat quietly filtered to one person.
  add(assignee.value, '')

  return {
    named,
    others: [...others.values()].sort((a, b) => a.label.localeCompare(b.label)),
  }
})

// Assignment is the shorter list again: see ASSIGNABLE_USERNAMES.
const assignableUsers = computed(
  () => users.value.filter(u => ASSIGNABLE_USERNAMES.includes(u.username)),
)

// The short list, plus whoever already owns this row. A dropdown that
// quietly drops its own current value is how an owner gets erased by
// someone opening the menu and picking the wrong line -- which is exactly
// what happened to 22 rows once already.
function memberChoices(row) {
  const chosen = row.assignee || null
  if (!chosen || assignableUsers.value.some(u => u.id === chosen)) {
    return assignableUsers.value
  }
  const known = users.value.find(u => u.id === chosen)
  const name = (known && known.label) || row.assignee_display || `User ${chosen}`
  return [...assignableUsers.value, { id: chosen, label: `${name} (current owner)` }]
}

function avatarClass(name) {
  let hash = 0
  for (const ch of name) hash = (hash + ch.charCodeAt(0)) % AVATARS.length
  return AVATARS[hash]
}

// --- selection -----------------------------------------------------------

function toggleRow(row, checked) {
  // Reassigning the Set rather than mutating it, so the computed template
  // bindings that read it actually re-evaluate.
  const next = new Set(selectedIds.value)
  if (checked) next.add(row.id)
  else next.delete(row.id)
  selectedIds.value = next
}

function groupFullySelected(group) {
  return group.rows.every(row => selectedIds.value.has(row.id))
}

function toggleGroup(group, checked) {
  const next = new Set(selectedIds.value)
  for (const row of group.rows) {
    if (checked) next.add(row.id)
    else next.delete(row.id)
  }
  selectedIds.value = next
}

function clearSelection() {
  selectedIds.value = new Set()
  bulkStatus.value = ''
  bulkFailed.value = 0
}

async function applyBulkStatus() {
  if (!bulkStatus.value) return
  bulkRunning.value = true
  bulkDone.value = 0
  bulkFailed.value = 0
  error.value = ''

  // Sequential rather than parallel: a bulk change of 50 rows should not
  // open 50 connections, and a partial failure is easier to report when
  // each row is resolved before the next starts.
  for (const id of [...selectedIds.value]) {
    const row = items.value.find(r => r.id === id)
    if (!row || row.status === bulkStatus.value) {
      bulkDone.value += 1
      continue
    }
    const previous = row.status
    row.status = bulkStatus.value
    try {
      const updated = await updateWorkItem(token(), id, { status: bulkStatus.value })
      Object.assign(row, updated)
    } catch (e) {
      row.status = previous
      bulkFailed.value += 1
      error.value = describe(e)
    } finally {
      bulkDone.value += 1
    }
  }

  bulkRunning.value = false
  if (!bulkFailed.value) clearSelection()
}

// --- inline edits --------------------------------------------------------

async function startEditMember(row) {
  editingMember.value = row.id
  await nextTick()
  const el = Array.isArray(memberSelect.value) ? memberSelect.value[0] : memberSelect.value
  if (el && el.focus) el.focus()
}

// Double-clicked the cell while its own box is already open -- which is what
// double-clicking a word inside a text input is -- so there is nothing to do.
async function startEditTitle(row) {
  if (editingTitle.value === row.id) return
  editingTitle.value = row.id
  titleDraft.value = row.title
  await nextTick()
  const el = Array.isArray(titleInput.value) ? titleInput.value[0] : titleInput.value
  if (el && el.focus) {
    el.focus()
    // Selected rather than placed at the end: a rename is more often a
    // rewrite than an append, and either way the first keystroke decides.
    if (el.select) el.select()
  }
}

function cancelEditTitle() {
  editingTitle.value = null
  titleDraft.value = ''
}

// Called by Enter and by losing focus, and Escape closes the box before the
// blur reaches here -- so the guard is what makes Escape mean Escape.
// An emptied box is a cancel too: a work item has to have a title, and the
// server would only refuse it a round trip later.
async function commitTitle(row) {
  if (editingTitle.value !== row.id) return
  const next = titleDraft.value.trim()
  editingTitle.value = null
  titleDraft.value = ''
  if (!next || next === row.title) return
  await patch(row, { title: next })
}

async function changeMember(row, value) {
  const next = value === '' ? null : parseInt(value, 10)
  editingMember.value = null
  if (next === (row.assignee || null)) return
  await patch(row, { assignee: next })
}

function onSaved(updated) {
  const row = items.value.find(r => r.id === updated.id)
  if (row) Object.assign(row, updated)
  // A kind or title change can alter what is a legal parent elsewhere, so
  // keep the picker's own list in step without another round trip.
  const option = parentOptions.value.find(p => p.id === updated.id)
  if (option) Object.assign(option, pickParentOption(updated))
}

// The strip uploads straight to the server, so the row here is the stale
// copy. Patching it means reopening the modal shows what is actually
// attached, without refetching the board.
function onAttachmentsChanged(list) {
  if (!editing.value) return
  const row = items.value.find(r => r.id === editing.value.id)
  if (row) row.attachments = list
  editing.value.attachments = list
}

// --- drag to reorder and to move between sprints -------------------------

// Native drag-and-drop will not scroll this page upwards, which makes a row
// at the top of the window impossible to drop above: Chrome measures its own
// edge autoscroll from the real top of the viewport, and the navbar, the
// filter band and the pinned sprint strip cover that between them. The
// pointer runs out of page before it ever reaches the edge the browser is
// watching.
//
// So the page scrolls itself. The trigger zone begins below whatever is
// currently pinned rather than at the window edge, and the speed ramps with
// how far into it the pointer has gone, so a nudge creeps and a shove flies.
const AUTOSCROLL_ZONE = 140   // px of trigger zone at each edge
const AUTOSCROLL_MAX = 22     // px per frame at the far end of it

// Plain variables rather than refs: nothing renders off these, and a ref
// would turn every mouse move during a drag into a reactive write.
let dragPointerY = null
let autoScrollFrame = null

function dragPointerMoved(event) {
  dragPointerY = event.clientY
}

function autoScrollSpeed(depth) {
  return Math.ceil(AUTOSCROLL_MAX * Math.min(1, depth / AUTOSCROLL_ZONE))
}

// A frame loop, not a scroll inside the dragover handler: dragover stops
// firing the moment the pointer stops moving, and holding a row still
// against the top of the window is exactly how anybody expects to keep
// scrolling. The next frame is booked first so an early return cannot
// quietly end the drag.
function autoScrollTick() {
  autoScrollFrame = requestAnimationFrame(autoScrollTick)
  if (dragPointerY === null) return

  // Everything pinned above the rows, measured rather than assumed -- the
  // filter band grows a bulk bar, and a long sprint name wraps.
  const ceiling = NAVBAR_HEIGHT + topStickyHeight.value + sprintStripHeight.value
  const above = (ceiling + AUTOSCROLL_ZONE) - dragPointerY
  const below = dragPointerY - (window.innerHeight - AUTOSCROLL_ZONE)

  if (above > 0) window.scrollBy(0, -autoScrollSpeed(above))
  else if (below > 0) window.scrollBy(0, autoScrollSpeed(below))
}

// Listened for on the document in the capture phase, for two reasons: an
// upward drag spends most of its time over the sticky band rather than over
// any row, and the row handlers call .stop, so a bubbling listener would
// never see the drop that ends the drag.
const AUTOSCROLL_EVENTS = { capture: true }

function startAutoScroll() {
  if (autoScrollFrame !== null) return
  dragPointerY = null
  document.addEventListener('dragover', dragPointerMoved, AUTOSCROLL_EVENTS)
  document.addEventListener('drop', stopAutoScroll, AUTOSCROLL_EVENTS)
  document.addEventListener('dragend', stopAutoScroll, AUTOSCROLL_EVENTS)
  autoScrollFrame = requestAnimationFrame(autoScrollTick)
}

function stopAutoScroll() {
  if (autoScrollFrame !== null) cancelAnimationFrame(autoScrollFrame)
  autoScrollFrame = null
  dragPointerY = null
  document.removeEventListener('dragover', dragPointerMoved, AUTOSCROLL_EVENTS)
  document.removeEventListener('drop', stopAutoScroll, AUTOSCROLL_EVENTS)
  document.removeEventListener('dragend', stopAutoScroll, AUTOSCROLL_EVENTS)
}

function startDrag(row) {
  // Dragging a selected row drags the whole selection; dragging an
  // unselected row drags just that one and leaves the selection alone.
  const group = groups.value.find(g => g.rows.some(r => r.id === row.id))
  draggingIds.value = dragSet(row, selectedIds.value, group ? group.rows : [row])
  startAutoScroll()
}

function endDrag() {
  draggingIds.value = []
  dropTarget.value = null
  dropRowId.value = null
  stopAutoScroll()
}

function groupOf(rowId) {
  return groups.value.find(g => g.rows.some(r => r.id === rowId)) || null
}

// Inside one owner's section a reorder still lands where you aimed it: the
// sections hold their rows in rank order, so a row placed before the target
// in rank is also drawn before it on screen. Between two owners it is not --
// the row would be filed under its own owner, somewhere else entirely -- so
// those drops are declined rather than guessed at, and the drop line is
// withheld to say so before the mouse is released.
function sameOwner(left, right) {
  return (left.assignee || null) === (right.assignee || null)
}

function reorderableWithin(target) {
  // every() on an empty list is true, and a drag begun outside the page --
  // a file dropped onto the window -- carries no rows of ours.
  if (!draggingIds.value.length) return false
  const destination = groupOf(target.id)
  if (!destination) return false
  return draggingIds.value.every(id => {
    const row = items.value.find(r => r.id === id)
    const source = groupOf(id)
    return Boolean(row) && sameOwner(row, target)
      && Boolean(source) && source.key === destination.key
  })
}

function rowDragOver(row) {
  // Ungrouped, every row is a legal target; grouped, only its own section.
  if (groupByOwner.value && !reorderableWithin(row)) {
    const group = groupOf(row.id)
    dropTarget.value = group ? group.key : null
    dropRowId.value = null
    return
  }
  dropRowId.value = row.id
}

async function dropOnRow(target) {
  // Not a move within one section: the only thing left that means anything
  // is a change of sprint, and dropAcrossSprints declines the rest.
  if (groupByOwner.value && !reorderableWithin(target)) {
    await dropAcrossSprints(groupOf(target.id))
    return
  }

  const ids = [...draggingIds.value]
  dropRowId.value = null
  dropTarget.value = null
  if (!ids.length) return

  const source = groupOf(ids[0])
  const destination = groupOf(target.id)
  if (!destination) return

  const sameSprint = source && source.key === destination.key
  if (sameSprint) {
    // Reorder within the sprint: land immediately before the target.
    await applyWrites(planReorder(destination.rows, ids, target.id))
  } else {
    // Crossing sprints always lands at the front, wherever it was dropped.
    await moveToSprint(ids, destination.sprintId, destination.rows)
  }
}

// The only move that still means something while grouped by owner: which
// sprint a row belongs to is independent of the order within it. A drop
// back into the sprint it came from is declined rather than quietly sent to
// the front of a list that is not in that order on screen.
async function dropAcrossSprints(destination) {
  const ids = [...draggingIds.value]
  dropRowId.value = null
  dropTarget.value = null
  if (!ids.length || !destination) return
  const source = groupOf(ids[0])
  if (source && source.key === destination.key) return
  await moveToSprint(ids, destination.sprintId, destination.rows)
}

async function dropOn(group) {
  if (groupByOwner.value) {
    await dropAcrossSprints(group)
    return
  }

  const ids = [...draggingIds.value]
  dropTarget.value = null
  dropRowId.value = null
  if (!ids.length) return

  const source = groupOf(ids[0])
  if (source && source.key === group.key) {
    // Dropped on the sprint itself rather than a row: send to the front.
    await applyWrites(planReorder(group.rows, ids, null))
    return
  }
  await moveToSprint(ids, group.sprintId, group.rows)
}

async function moveToSprint(ids, sprintId, destinationRows) {
  const ranks = new Map(
    planMoveToFront(destinationRows, ids).map(w => [w.id, w.rank]),
  )
  for (const id of ids) {
    const row = items.value.find(r => r.id === id)
    if (!row) continue
    await patch(row, { sprint: sprintId, rank: ranks.get(id) })
  }
}

async function applyWrites(writes) {
  // Sequential: a renumber can touch many rows, and a partial failure is
  // only reportable if each write resolves before the next begins.
  for (const write of writes) {
    const row = items.value.find(r => r.id === write.id)
    if (!row || row.rank === write.rank) continue
    await patch(row, { rank: write.rank })
  }
}

// --- io ------------------------------------------------------------------

async function patch(row, payload) {
  saving.value = row.id
  error.value = ''
  const previous = { ...row }
  Object.assign(row, payload)
  try {
    const updated = await updateWorkItem(token(), row.id, payload)
    Object.assign(row, updated)
  } catch (e) {
    // Roll the row back so the table never shows a state the server refused.
    Object.assign(row, previous)
    error.value = describe(e)
  } finally {
    saving.value = null
  }
}

// The board asks for the sprint window it is showing, not for the backlog.
// Importing the Airtable archive took the table past 2,600 work items, and
// get_all=true was fetching every one of them -- about 1.9MB per load -- to
// show the thirty in the current sprint.
//
// Which means every filter the board offers has to be a server filter. A
// page of a filtered set is right; a page filtered in the browser is wrong,
// because it would drop rows from page one and leave the matches sitting on
// page four with nothing to say so.
function filterParams() {
  const params = { ordering: 'rank', page_size: PAGE_SIZE }
  if (search.value) params.search = search.value
  if (kind.value) params.kind = kind.value
  if (status.value.length) params.status = status.value.join(',')
  if (assignee.value) params.assignee = assignee.value
  if (overlay.value) params.overlay = overlay.value

  // Null means there are no dated sprints to anchor a window on, so there
  // is no window to ask for. 'none' carries the sprintless pile, which sits
  // outside any window and has its own toggle.
  const window = sprintsInWindow.value
  if (window !== null) {
    const tokens = [...window]
    if (showNoSprint.value) tokens.push('none')
    params.sprints = tokens.join(',')
  }
  return params
}

function applyPage(data, append) {
  const rows = Array.isArray(data) ? data : (data.results || [])
  items.value = append ? [...items.value, ...rows] : rows
  matching.value = Array.isArray(data)
    ? items.value.length
    : (data.count ?? items.value.length)
  // Page numbers are counted here rather than parsed out of the next link:
  // the API's own `page` field reports the page either side of the current
  // one, which is not what a "load the next one" button needs.
  pagesLoaded.value = append ? pagesLoaded.value + 1 : 1
  hasMore.value = Boolean(!Array.isArray(data) && data.next)
  // Drop selections for rows that are no longer on the page, so a bulk
  // action cannot silently touch something you can no longer see.
  const present = new Set(items.value.map(r => r.id))
  selectedIds.value = new Set([...selectedIds.value].filter(id => present.has(id)))
}

// Counted on the server and nothing written, so the board can name the
// number before it asks. Worth the extra round trip: "are you sure" is a
// question nobody reads, and "clear the sprint on 69 items?" is one they do.
async function askSweepBacklogSprints() {
  sweeping.value = true
  error.value = ''
  sweptNote.value = ''
  try {
    const preview = await clearBacklogSprints(token(), { dryRun: true })
    sweepCount.value = preview.count
  } catch (e) {
    error.value = describe(e)
  } finally {
    sweeping.value = false
  }
}

async function sweepBacklogSprints() {
  sweeping.value = true
  error.value = ''
  try {
    const result = await clearBacklogSprints(token())
    sweepCount.value = null
    // Reloaded before the note is written, because the items just left the
    // sprint window the board is showing: what is on screen is out of date
    // in exactly the way this button was pressed to make it. load() clears
    // the note, so it is set after.
    await load()
    sweptNote.value = `${result.cleared} item${result.cleared === 1 ? '' : 's'} `
      + 'moved off their sprints'
  } catch (e) {
    error.value = describe(e)
  } finally {
    sweeping.value = false
  }
}

async function load() {
  loading.value = true
  error.value = ''
  // Whatever the last sweep reported is about the board as it was, not as
  // it is about to be.
  sweptNote.value = ''
  try {
    applyPage(await getWorkItems(token(), { ...filterParams(), page: 1 }), false)
  } catch (e) {
    error.value = describe(e)
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!hasMore.value || loadingMore.value) return
  loadingMore.value = true
  error.value = ''
  try {
    const page = pagesLoaded.value + 1
    applyPage(await getWorkItems(token(), { ...filterParams(), page }), true)
  } catch (e) {
    error.value = describe(e)
  } finally {
    loadingMore.value = false
  }
}

// --- live updates --------------------------------------------------------
//
// Several people plan from this board at once, so the useful question is
// not "what did I just change" but "what has anyone changed". There is no
// socket in this stack, so it polls -- and only on request, for five
// minutes. A board left polling on a forgotten tab is a request every five
// seconds against production for as long as the laptop stays open.
const POLL_EVERY_MS = 5000
const POLL_FOR_MS = 5 * 60 * 1000
// Clicking the button again while it runs buys another five minutes. The
// ceiling is the point: this is a request against production every five
// seconds, and a clock that can be topped up without limit is a board left
// polling through lunch.
const POLL_MAX_MS = 15 * 60 * 1000
// Long enough to catch the eye, and over before the next poll, so two
// flashes never run together and read as one long highlight.
const FLASH_FOR_MS = 2000

const polling = ref(false)
const pollRemainingMs = ref(0)
const pollLastAt = ref('')
const changedIds = ref(new Set())

let pollTimer = null
let countdownTimer = null
let flashTimer = null
let pollInFlight = false
// When this run ends, as wall-clock time. Kept out here rather than closed
// over inside startPolling so that a second click can push it later.
let pollEndsAt = 0

const pollRemainingLabel = computed(() => {
  const seconds = Math.max(0, Math.ceil(pollRemainingMs.value / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
})

// A second's slack, because the countdown only ticks once a second: without
// it the button would claim there was room for another five minutes for up
// to a second after the clock had been pushed to the ceiling.
const pollExtendable = computed(() => pollRemainingMs.value < POLL_MAX_MS - 1000)

// One tint per row, decided here rather than by stacking three bg- classes
// on the element: Tailwind utilities of equal specificity are settled by
// their order in the stylesheet, not by the order they appear in :class, so
// "selected and just changed" would have been a coin toss. Saving is the
// most urgent thing a row can be doing, then being selected, then having
// just changed under you.
function rowTint(row) {
  if (saving.value === row.id) return 'bg-yellow-50'
  if (selectedIds.value.has(row.id)) return 'bg-blue-50'
  if (changedIds.value.has(row.id)) return 'bg-amber-100'
  return ''
}

// What makes a row worth flashing. updated_at covers every stored field, so
// one comparison catches a status, an owner, a title or a move between
// sprints. The overlays are derived from other tables -- raising a blocker
// does not touch the work item's own row -- so they are compared too.
const WATCHED = ['updated_at', 'is_blocked', 'is_flagged']

function rowDiffers(existing, incoming) {
  return WATCHED.some(field => existing[field] !== incoming[field])
}

// Rows are merged into the ones already on screen rather than replacing
// them, because those objects are held elsewhere: the open modal is editing
// one, and a drag may be mid-flight against them. Same objects, new values.
function mergeRows(rows, count, more) {
  const existingById = new Map(items.value.map(row => [row.id, row]))
  const merged = []
  const changed = new Set()
  for (const incoming of rows) {
    const existing = existingById.get(incoming.id)
    if (!existing) {
      merged.push(incoming)
      changed.add(incoming.id)
      continue
    }
    if (rowDiffers(existing, incoming)) {
      Object.assign(existing, incoming)
      changed.add(existing.id)
    }
    merged.push(existing)
  }
  // A row that has left the window -- moved to another sprint, or changed
  // to a status the filters exclude -- simply goes. There is nothing to
  // flash on a row that is no longer there.
  items.value = merged
  matching.value = count
  hasMore.value = more
  const present = new Set(items.value.map(row => row.id))
  selectedIds.value = new Set([...selectedIds.value].filter(id => present.has(id)))
  return changed
}

function flash(ids) {
  if (!ids.size) return
  changedIds.value = ids
  if (flashTimer) clearTimeout(flashTimer)
  flashTimer = setTimeout(() => {
    changedIds.value = new Set()
    flashTimer = null
  }, FLASH_FOR_MS)
}

// Skipped rather than queued: the tick comes round again in five seconds,
// and rewriting rows while they are being dragged, bulk-updated or saved
// would fight the person doing it.
function busy() {
  return (
    loading.value || loadingMore.value || bulkRunning.value
    || saving.value !== null || draggingIds.value.length > 0
  )
}

async function poll() {
  if (pollInFlight || busy()) return
  pollInFlight = true
  const params = filterParams()
  const fingerprint = JSON.stringify(params)
  try {
    // Exactly the pages that are on screen. Asking for page one only would
    // silently throw away whatever Load more had fetched.
    const rows = []
    let count = 0
    let more = false
    for (let page = 1; page <= Math.max(1, pagesLoaded.value); page += 1) {
      const data = await getWorkItems(token(), { ...params, page })
      const batch = Array.isArray(data) ? data : (data.results || [])
      rows.push(...batch)
      count = Array.isArray(data) ? rows.length : (data.count ?? rows.length)
      more = Boolean(!Array.isArray(data) && data.next)
    }
    // The filters may have moved while this was in flight, in which case a
    // load() is already on its way and these are the wrong rows.
    if (JSON.stringify(filterParams()) !== fingerprint) return
    flash(mergeRows(rows, count, more))
    pollLastAt.value = new Date().toLocaleTimeString()
  } catch (e) {
    // Deliberately silent. A poll is a background courtesy: one that fails
    // should not replace the board with an error, and the next one is five
    // seconds away. Anything asked of the server directly still reports.
  } finally {
    pollInFlight = false
  }
}

function startPolling() {
  const now = Date.now()
  // Already running: top the clock up instead of starting a second one. Two
  // intervals would poll twice as often and only one would ever be cleared.
  // The ceiling is on what is left on the clock rather than on how long a
  // run may last: topping up never buys more than a quarter of an hour
  // ahead, but a board polled all afternoon is one topped up all afternoon,
  // and somebody is sitting in front of it to do that.
  if (polling.value) {
    pollEndsAt = Math.min(pollEndsAt + POLL_FOR_MS, now + POLL_MAX_MS)
    pollRemainingMs.value = Math.max(0, pollEndsAt - now)
    return
  }
  polling.value = true
  pollRemainingMs.value = POLL_FOR_MS
  // Counted against the clock rather than by decrementing, so a tab the
  // browser throttles in the background still stops on time.
  pollEndsAt = now + POLL_FOR_MS
  pollTimer = setInterval(poll, POLL_EVERY_MS)
  countdownTimer = setInterval(() => {
    pollRemainingMs.value = Math.max(0, pollEndsAt - Date.now())
    if (!pollRemainingMs.value) stopPolling()
  }, 1000)
  // Once straight away: waiting five seconds to see anything happen makes
  // the button feel broken.
  poll()
}

function stopPolling() {
  polling.value = false
  pollRemainingMs.value = 0
  pollEndsAt = 0
  if (pollTimer) clearInterval(pollTimer)
  if (countdownTimer) clearInterval(countdownTimer)
  pollTimer = null
  countdownTimer = null
}

// An interval outlives the component, and would go on polling against a
// board nobody is looking at.
onUnmounted(() => {
  stopPolling()
  stopAutoScroll()
  if (flashTimer) clearTimeout(flashTimer)
  if (sprintStripObserver) sprintStripObserver.disconnect()
})

// --- creating and archiving ----------------------------------------------

// A department is the one field a work item cannot do without, and on this
// board it is effectively always the same one. Read off the rows on screen
// rather than hardcoded, so it follows whatever this board is actually for
// instead of needing an edit when that changes.
function commonestDepartment() {
  const tally = new Map()
  for (const row of items.value) {
    if (!row.department) continue
    tally.set(row.department, (tally.get(row.department) || 0) + 1)
  }
  let best = null
  let most = 0
  for (const [id, count] of tally) {
    if (count > most) {
      best = id
      most = count
    }
  }
  return best
}

// A blank draft rather than a second form: the modal already knows how to
// show a work item, and a new one is a work item with nothing in it yet.
// It opens on the current sprint, because that is the one being looked at.
//
// Not Started rather than Backlog, and owned by whoever is making it: a
// story typed on this board is work somebody has decided to do, not an idea
// filed away unowned. Both are plain defaults in an open form -- the status
// and member selects are right there to change before saving.
function startCreate() {
  const anchor = currentSprintIndex.value
  const sprint = anchor === -1 ? null : sprintsAscending.value[anchor]
  creating.value = true
  editing.value = {
    id: null,
    kind: 'story',
    status: 'planned',
    title: '',
    description: '',
    sprint: sprint ? sprint.id : null,
    // Null until the user list has landed, which is also the only state in
    // which myUserId cannot name anybody -- the same fallback the board's
    // My-work button makes by hiding itself.
    assignee: myUserId.value,
    parent: null,
    department: commonestDepartment(),
    sub_department: null,
    target_quarter: '',
    target_date: null,
    visibility: 'internal',
    public_summary: '',
    public_eta: null,
    attachments: [],
  }
}

function closeModal() {
  editing.value = null
  creating.value = false
}

function onCreated(created) {
  // Onto the board directly rather than reloading it: a reload would also
  // throw away the pages that were loaded. It is shown even if the current
  // filters would exclude it -- having just made it, seeing it is the
  // point -- so the next load or poll is where it settles into place.
  items.value = [created, ...items.value]
  matching.value += 1
  if (['epic', 'milestone'].includes(created.kind)) {
    parentOptions.value = [...parentOptions.value, pickParentOption(created)]
  }
  flash(new Set([created.id]))
}

function onArchived(archived) {
  items.value = items.value.filter(row => row.id !== archived.id)
  matching.value = Math.max(0, matching.value - 1)
  const next = new Set(selectedIds.value)
  next.delete(archived.id)
  selectedIds.value = next
  parentOptions.value = parentOptions.value.filter(p => p.id !== archived.id)
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

function pickParentOption(row) {
  return { id: row.id, kind: row.kind, title: row.title }
}

// A task may hang off a story, and there are thousands of stories, so they
// are not preloaded with the epics and milestones. They are fetched the
// first time a task is opened for editing -- and only then, which for a
// backlog holding no tasks at all means never.
const storyParentsLoaded = ref(false)

async function ensureStoryParents() {
  if (storyParentsLoaded.value) return
  storyParentsLoaded.value = true
  try {
    const data = await getWorkItems(token(), {
      kind: 'story', ordering: 'title', page_size: 250,
    })
    const rows = Array.isArray(data) ? data : (data.results || [])
    parentOptions.value = [...parentOptions.value, ...rows.map(pickParentOption)]
  } catch (e) {
    // Non-fatal: the picker still offers the epics and milestones.
    storyParentsLoaded.value = false
  }
}

function openEdit(row) {
  if (row.kind === 'task') ensureStoryParents()
  editing.value = row
}

onMounted(async () => {
  try {
    sprints.value = await getSprints(token())
  } catch (e) {
    // Non-fatal: groups still form from the rows themselves.
  }
  try {
    departments.value = await getDepartments(token())
  } catch (e) {
    // Non-fatal: the edit form keeps the item's current department.
  }
  try {
    // Unfiltered by the board's own filters on purpose: the picker has to
    // offer every legal parent, including ones the current window hides.
    // Only the kinds that can BE a parent though -- epics and milestones,
    // a few dozen rows. This used to fetch the whole table, which after the
    // archive import meant 2,600 rows to populate a dropdown. Stories can
    // parent tasks, so they load on demand; see ensureStoryParents.
    const data = await getWorkItems(token(), {
      get_all: 'true', kind: 'epic,milestone',
    })
    const rows = Array.isArray(data) ? data : (data.results || [])
    parentOptions.value = rows.map(pickParentOption)
  } catch (e) {
    // Non-fatal: the parent picker falls back to showing nothing to pick.
  }
  try {
    // getAllUsersWithGroups hands back the whole axios response, and
    // swallows its own failures by resolving with the error object -- so
    // `.results` was read off a response that never had it, every user
    // dropped on the floor, and the catch below never ran to say so. With
    // an empty list the Member dropdown offered nothing but "unassigned",
    // which is why an owner could not be set at all. Unwrapped the way
    // every other caller of this service does it.
    const response = await getAllUsersWithGroups(token())
    const payload = response && response.data
    const rows = Array.isArray(payload) ? payload : ((payload || {}).results || [])
    users.value = rows
      .map(u => ({
        id: u.id,
        username: u.username,
        label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch (e) {
    // Non-fatal: the member cell falls back to showing the current value.
  }
  await load()
})
</script>
